import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import React from "react";
import TiersSummary from "features/affiliation/common/Tiers/TiersSummary";
import Balance from "features/affiliation/common/Balance";
import Card from "common/Boxes/Card";
import { SmartTableLayout } from "common/Layouts/SmartTableLayout";
import styled, { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { LARGE_MOBILE, LARGE_TABLET } from "common/constants";
import {
  AFFILIATION_SMALL_SCREEN,
  MINIFIED_TIERS,
} from "features/affiliation/common/constants";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface CardsAndTableLayoutProps {
  loading: boolean;
  table?: React.JSX.Element;
  tableTitle?: string;
  statistics?: React.JSX.Element;
  graph?: React.JSX.Element;
}

const CardsAndTableLayout = ({
  loading,
  table,
  tableTitle,
  statistics,
  graph,
}: CardsAndTableLayoutProps) => {
  const theme = useTheme();
  const isSmallScreen = useMedia(
    [`(max-width: ${AFFILIATION_SMALL_SCREEN})`],
    [true],
    false
  );
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const isTablet = useMedia([`(max-width: ${LARGE_TABLET})`], [true], false);
  const isMinifiedTiers = useMedia(
    [`(max-width: ${MINIFIED_TIERS})`],
    [true],
    false
  );
  const showBalance = !isSmallScreen || !statistics;

  usePageWrapper({
    showSearch: true,
  });

  return (
    <LoadingSpinnerOverlay
      isLoading={loading}
      hideContentWhileLoading={true}
      spinnerMarginTop={50}
      spinnerSize="large"
      wrap={true}
      backgroundColor={theme.colors.layoutBgColor}
      wrapStyle={{ height: "auto" }}
    >
      <CardsWrapper
        $showGraph={!!graph}
        $isSmallScreen={isSmallScreen}
        $isMobile={isMobile}
        $isTablet={isTablet}
      >
        <TiersSummary
          isMinified={true}
          hideNumUsers={isMinifiedTiers && !isMobile}
        />
        {showBalance && (
          <BalanceWrapper $isMobile={isMobile}>
            <Balance />
          </BalanceWrapper>
        )}
        {statistics && (
          <StatisticsCard $isTablet={isTablet} $isMobile={isMobile}>
            {statistics}
          </StatisticsCard>
        )}
        {table && (
          <TableCard
            $showGraph={!!graph}
            $isSmallScreen={isSmallScreen}
            $isMobile={isMobile}
          >
            <SmartTableLayout
              tableComponent={table}
              titleOrNode={tableTitle}
              withSearch={true}
            />
          </TableCard>
        )}
        {graph && !isSmallScreen && (
          <GraphWrapper $showStats={!!statistics}>{graph}</GraphWrapper>
        )}
      </CardsWrapper>
    </LoadingSpinnerOverlay>
  );
};

export default CardsAndTableLayout;

const CardsWrapper = styled.div<{
  $showGraph: boolean;
  $isSmallScreen: boolean;
  $isMobile: boolean;
  $isTablet: boolean;
}>`
  display: grid;
  gap: ${(p) => (p.$isTablet ? 12 : 16)}px;
  margin-top: 32px;
  grid-template-columns: 3fr 2fr 32%;
  grid-template-rows: auto 1fr;

  ${(p) =>
    p.$showGraph &&
    `
    grid-template-rows: auto 1fr 1fr 1fr;
  `};
  ${(p) =>
    p.$isSmallScreen &&
    `
    grid-template-columns: 1fr 1fr;
  `};
  ${(p) =>
    p.$isMobile &&
    `
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
    column-gap: 0;
  `};

  @media (max-width: ${phoneMaxWidth}) {
    margin-top: 16px;
  }
`;
const StatisticsCard = styled(Card)<{ $isTablet: boolean; $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 95px;
  padding: 12px;
  ${(p) =>
    p.$isMobile &&
    `
    grid-row: 2;
  `};
`;
const TableCard = styled(Card)<{
  $showGraph: boolean;
  $isSmallScreen: boolean;
  $isMobile: boolean;
}>`
  grid-column: 1 / ${(p) => (p.$showGraph || p.$isSmallScreen ? 3 : 4)};
  grid-row: 2 / ${(p) => (p.$showGraph ? 5 : "auto")};
  ${(p) =>
    p.$isMobile &&
    `
    grid-row: 3;
  `};
`;
const GraphWrapper = styled.div<{ $showStats: boolean }>`
  grid-row: ${(p) => (p.$showStats ? 2 : 1)} / 5;
  grid-column: 3;
`;
const BalanceWrapper = styled.div<{ $isMobile: boolean }>`
  ${(p) =>
    p.$isMobile &&
    `
    grid-row: 2 / 3;
  `};
`;
